import { useMutation } from "@tanstack/react-query";
import { updateWorkoutItem } from "api-client";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Label, Textarea } from "ui";
import { useDebouncedCallback } from "use-debounce";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";

interface ExerciseNoteTextareaProps {
  workoutItemId: string;
  editable?: boolean;
  className?: string;
}

export const ExerciseNoteTextarea = observer(function ExerciseNoteTextarea(
  props: ExerciseNoteTextareaProps,
) {
  const { t } = useTranslation();
  const { withSaving } = useAuthenticatedNavigationContext();
  const workoutEditStore = useWorkoutEditStore();
  const workoutItem = workoutEditStore.getWorkoutItemById(props.workoutItemId);

  const updateNoteMutation = useMutation({
    mutationFn: async (instructions: string | null) => {
      return await withSaving(
        updateWorkoutItem({
          path: {
            workoutId: workoutEditStore.workout.id,
            workoutItemId: props.workoutItemId,
          },
          body: { instructions },
        }),
      );
    },
  });

  const debouncedUpdate = useDebouncedCallback((value: string) => {
    const trimmed = value.trim();
    updateNoteMutation.mutate(trimmed.length ? value : null);
  }, 400);

  useEffect(() => {
    return () => {
      debouncedUpdate.flush();
    };
  }, [debouncedUpdate]);

  function handleNoteChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    if (!props.editable) {
      return;
    }

    const value = event.target.value;

    workoutEditStore.updateWorkoutItem(props.workoutItemId, {
      instructions: value,
    });

    debouncedUpdate(value);
  }

  const note = workoutItem.instructions ?? "";
  return (
    <div className="flex flex-col gap-2 w-full mt-2">
      <Label className="text-xs font-medium text-muted-foreground">
        {t("exerciseNotes.label")}
      </Label>
      {props.editable ? (
        <Textarea
          value={note}
          placeholder={t("notes.addPlaceholder")}
          onChange={handleNoteChange}
          className="min-h-[unset] bg-muted/50 border-0 focus:bg-muted rounded px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground/60"
        />
      ) : (
        <div className="min-h-[36px] px-3 py-2 bg-muted/50 rounded">
          <span className="text-sm text-foreground whitespace-pre-wrap break-words">
            {note.length ? (
              note
            ) : (
              <span className="text-muted-foreground italic">-</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
});
